import Button from 'tio-ui/components/button';
import Chip from 'tio-ui/components/chip';
import { concat } from '@ember/helper';
import { dropTask } from 'ember-concurrency';
import EditPopover from './edit-popover.gts';
import { formatCamelCase } from '../../utils/format';
import Input from 'tio-ui/components/input';
import Select from 'tio-ui/components/select';
import Textarea from 'tio-ui/components/textarea';
import { not, or } from 'tio-ui/utilities';
import Popover from 'tio-ui/components/popover';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import { trackedFunction } from 'reactiveweb/function';
import Component from '@glimmer/component';
import Table from 'tio-ui/components/table';
import TermVerified from '../profile/term-verified.gts';
import ErrorMessages from 'tio-ui/components/error-messages';
import type { NotificationsService } from 'tio-ui/components/notifications';
import type { Company as CompanyModel } from 'tio-data/data/resources/company.type';
import type { EmployeeExtension as EmployeeModel } from 'tio-data/data/resources/employee.ext';
import type Intl from 'ember-intl/services/intl';
import type Owner from '@ember/owner';
import type { PlanExtension as PlanModel } from 'tio-data/data/resources/plan.ext';
// This is for types. Anything we have custom may show an error.
import type { Store } from '@warp-drive/core';
import { findRecord } from '@warp-drive/utilities/json-api';
import { updateOverridesRequest } from 'tio-data/requests/employee-actions';
import { PhPencil, PhX, PhCheck } from 'ember-phosphor-icons';
import { reloadRecord } from 'tio-data/requests/save-record';

export interface UpdateEmployeeOverrideTableSignature {
  Args: {
    employee: EmployeeModel;
    pendingOverrideField?: string;
    onPendingOverrideHandled?: () => void;
  };
  Element: HTMLDivElement;
}

export default class UpdateEmployeeOverrideTable extends Component<UpdateEmployeeOverrideTableSignature> {
  get cpkFieldName() {
    return 'contributionPlanKey';
  }

  get contributionPlanKey() {
    return this.args.employee.employeeAddition?.processed?.merged?.[this.cpkFieldName] || '';
  }

  get isContributionPlanKeyOverride() {
    if (!Array.isArray(this.args.employee.employeeAddition?.overrides)) {
      return false;
    }
    return this.args.employee.employeeAddition.overrides.some(
      ({ field }) => field === this.cpkFieldName
    );
  }

  get contributionAmountFieldName() {
    return 'contributionAmount';
  }

  get contributionAmount() {
    return (
      this.args.employee.employeeAddition?.processed?.merged?.[this.contributionAmountFieldName] ??
      ''
    );
  }

  get isContributionAmountOverride() {
    if (!Array.isArray(this.args.employee.employeeAddition?.overrides)) {
      return false;
    }
    return this.args.employee.employeeAddition.overrides.some(
      ({ field }) => field === this.contributionAmountFieldName
    );
  }

  get pendingFieldIsNew() {
    const pending = this.args.pendingOverrideField;
    if (!pending) return false;
    const merged = this.args.employee.employeeAddition?.editableMergedFields;
    if (!merged) return true;
    return !(pending in merged);
  }

  get pendingOverrideFieldName(): string {
    return this.args.pendingOverrideField || '';
  }

  <template>
    <Table class="!min-w-0">
      <:tbody as |options|>
        <options.tr>
          <options.td class={{options.cellClass}}>
            {{t "common.employees.fields.custom_contribution_amount"}}
          </options.td>
          <options.td class={{options.cellClass}}>
            {{String this.contributionAmount}}
          </options.td>
          <options.td class={{options.cellClass}}>
            <div class="tio-flex-end">
              {{#if this.isContributionAmountOverride}}
                <div class="tio-row-loose">
                  <Chip @size="sm" @intent="default">{{t "common.override"}}</Chip>
                  <RevertOverridePopover
                    @fieldName={{this.contributionAmountFieldName}}
                    @employee={{@employee}}
                  />
                </div>
              {{/if}}
              <EditContributionAmount
                @value={{String this.contributionAmount}}
                @employee={{@employee}}
                @isOverride={{this.isContributionAmountOverride}}
              />
            </div>
          </options.td>
        </options.tr>
        <options.tr>
          <options.td class={{options.cellClass}}>
            {{t "common.employees.fields.contribution_plan_key"}}
          </options.td>
          <options.td class={{options.cellClass}}>
            {{String this.contributionPlanKey}}
          </options.td>
          <options.td class={{options.cellClass}}>
            <div class="tio-flex-end">
              {{#if this.isContributionPlanKeyOverride}}
                <div class="tio-row-loose">
                  <Chip @size="sm" @intent="default">{{t "common.override"}}</Chip>
                  <RevertOverridePopover @fieldName={{this.cpkFieldName}} @employee={{@employee}} />
                </div>
              {{/if}}
              <EditContributionPlanKey
                @employee={{@employee}}
                @value={{String this.contributionPlanKey}}
              />
            </div>
          </options.td>
        </options.tr>
        {{#each-in @employee.employeeAddition.editableMergedFields as |key value|}}
          <UpdateOverrideRow
            @value={{value}}
            @fieldName={{key}}
            @employee={{@employee}}
            @cellClass={{options.cellClass}}
            @autoOpenField={{@pendingOverrideField}}
            @onAutoOpenClose={{@onPendingOverrideHandled}}
          />
        {{/each-in}}
        {{#if this.pendingFieldIsNew}}
          <UpdateOverrideRow
            @value=""
            @fieldName={{this.pendingOverrideFieldName}}
            @employee={{@employee}}
            @cellClass={{options.cellClass}}
            @autoOpenField={{@pendingOverrideField}}
            @onAutoOpenClose={{@onPendingOverrideHandled}}
          />
        {{/if}}
        <options.tr>
          <options.td class={{options.cellClass}}>
            <TermVerified @employee={{@employee}} />
          </options.td>
        </options.tr>
      </:tbody>
    </Table>
  </template>
}

interface UpdateOverrideRowSignature {
  Args: {
    value: unknown;
    fieldName: string;
    employee: EmployeeModel;
    cellClass: string;
    autoOpenField?: string;
    onAutoOpenClose?: () => void;
  };
  Element: HTMLDivElement;
}

class UpdateOverrideRow extends Component<UpdateOverrideRowSignature> {
  get isOverride() {
    const { overrides } = this.args.employee.employeeAddition;

    // Adding this check to fix an issue with bad data coming from the API
    if (!Array.isArray(overrides)) {
      return false;
    }

    return !!overrides.find(({ field }) => field === this.args.fieldName);
  }

  get overrideCellClass() {
    return this.args.cellClass + ' flex justify-end';
  }

  <template>
    <tr>
      <td class={{@cellClass}}>{{formatCamelCase @fieldName}}</td>
      <td class={{@cellClass}}>{{String @value}}</td>
      <td class={{this.overrideCellClass}}>
        {{#if this.isOverride}}
          <div class="tio-row-loose">
            <Chip @size="sm" @intent="default">{{t "common.override"}}</Chip>
            <RevertOverridePopover @fieldName={{@fieldName}} @employee={{@employee}} />
          </div>
        {{/if}}
        <EditFieldPopover
          @fieldName={{@fieldName}}
          @value={{@value}}
          @employee={{@employee}}
          @autoOpen={{eq @fieldName @autoOpenField}}
          @onAutoOpenClose={{@onAutoOpenClose}}
        />
      </td>
    </tr>
  </template>
}

export interface EditFieldPopoverSignature {
  Args: {
    fieldName: string;
    value: unknown;
    employee: EmployeeModel;
    autoOpen?: boolean;
    onAutoOpenClose?: () => void;
  };
  Element: HTMLDivElement;
}

export class EditFieldPopover extends Component<EditFieldPopoverSignature> {
  @tracked isOpen = false;
  @tracked editableValue!: unknown;
  @tracked reasonForEdit = '';
  @tracked submitError?: string;
  @tracked didSubmit = false;

  @service declare store: Store;
  @service declare notifications: NotificationsService;
  @service declare intl: Intl;

  constructor(owner: Owner, args: EditFieldPopoverSignature['Args']) {
    super(owner, args);
    this.syncEditableValue();
  }

  get effectiveIsOpen() {
    return this.isOpen || !!this.args.autoOpen;
  }

  get isValid() {
    // TODO: Add type validations
    return !!this.reasonForEdit && !!this.editableValue && this.editableValue !== this.args.value;
  }

  syncEditableValue() {
    this.editableValue = this.args.value;
    this.reasonForEdit = '';
  }

  handleOpen = () => {
    this.isOpen = true;
  };

  updateReasonForEdit = (reason: string) => {
    this.reasonForEdit = reason;
  };

  setEditableValue = (value: unknown) => {
    this.editableValue = value;
  };

  cancel = () => {
    this.submitError = '';
    this.didSubmit = false;
    this.isOpen = false;
    this.syncEditableValue();
    if (this.args.autoOpen) {
      this.args.onAutoOpenClose?.();
    }
  };

  updateTask = dropTask(async () => {
    this.submitError = '';
    this.didSubmit = false;
    try {
      const fieldName = this.args.fieldName;
      await this.store.request(
        updateOverridesRequest(
          this.args.employee.id!,
          { field: fieldName, value: this.editableValue },
          this.reasonForEdit
        )
      );
      // Close and clear pending state BEFORE reload to prevent a second modal
      this.isOpen = false;
      this.submitError = '';
      this.syncEditableValue();
      this.didSubmit = false;
      if (this.args.autoOpen) {
        this.args.onAutoOpenClose?.();
      }
      const successMessage = this.intl.t('common.employees.fields.update_success', {
        field: formatCamelCase(fieldName),
      });
      this.notifications.add(successMessage, {
        appearance: 'success',
      });
      await reloadRecord(this.store, this.args.employee.employeeAddition);
    } catch (e) {
      console.error(e);
      this.didSubmit = true;
      this.submitError = this.intl.t('common.employees.fields.update_error', {
        field: formatCamelCase(this.args.fieldName),
      });
    }
  });

  <template>
    <Popover @isOpen={{this.effectiveIsOpen}} @onOpen={{this.handleOpen}} @onClose={{this.cancel}}>
      <:trigger as |pop|>
        <Button
          @appearance="minimal"
          @intent="primary"
          title={{t "common.edit"}}
          {{pop.register}}
          {{on "click" pop.toggle}}
          data-test-update-override
        >
          <PhPencil @size="16" />
        </Button>
      </:trigger>
      <:body>
        <form class="tio-col-loose p-4">
          <h2 class="tio-text-xl-semibold">{{t "common.edit"}}
            {{formatCamelCase @fieldName}}</h2>
          <span class="tio-fg-muted text-sm">
            {{t "common.employees.fields.update_override_disclaimer"}}
          </span>
          <Input
            data-test-update-override-input
            @isDisabled={{this.updateTask.isRunning}}
            @value={{String this.editableValue}}
            @label={{formatCamelCase @fieldName}}
            @onChange={{this.setEditableValue}}
          />
          <Textarea
            data-test-reason-for-edit
            disabled={{this.updateTask.isRunning}}
            @value={{this.reasonForEdit}}
            @onChange={{this.updateReasonForEdit}}
            @label={{concat (t "common.comment") "*"}}
            @classes={{hash base="text-xs"}}
          />
          <div class="tio-flex-tight justify-end">
            <Button
              data-test-update-override-cancel
              class="w-auto"
              @intent="danger"
              {{on "click" this.cancel}}
              disabled={{this.updateTask.isRunning}}
              title={{t "common.cancel"}}
            >
              <PhX @size="16" />
            </Button>
            <Button
              data-test-update-override-save
              class="w-auto"
              @intent="primary"
              {{on "click" this.updateTask.perform}}
              @isRunning={{this.updateTask.isRunning}}
              disabled={{not this.isValid}}
              title={{t "common.save"}}
            >
              <PhCheck @size="16" />
            </Button>
          </div>
          <ErrorMessages
            @showErrors={{this.didSubmit}}
            @error={{this.submitError}}
            class="text-xs"
          />
        </form>
      </:body>
    </Popover>
  </template>
}

interface RevertOverridePopoverSignature {
  Args: {
    fieldName: string;
    employee: EmployeeModel;
  };
  Element: HTMLDivElement;
}

class RevertOverridePopover extends Component<RevertOverridePopoverSignature> {
  @tracked error?: string;

  @service declare store: Store;
  @service declare notifications: NotificationsService;
  @service declare intl: Intl;

  revertTask = dropTask(async (reasonForEdit: string) => {
    this.error = undefined;
    await this.store.request(
      updateOverridesRequest(
        this.args.employee.id!,
        { field: this.args.fieldName, value: null },
        reasonForEdit
      )
    );
    await reloadRecord(this.store, this.args.employee, { include: 'employee-addition,plan' });
    const successMessage = this.intl.t('common.employees.fields.revert_success', {
      field: formatCamelCase(this.args.fieldName),
    });
    this.notifications.add(successMessage, {
      appearance: 'success',
    });
  });

  handleSubmit = async (reasonForEdit: string) => {
    try {
      await this.revertTask.perform(reasonForEdit);
    } catch (e) {
      console.error(e);
      this.error = e instanceof Error ? e.message : String(e);
    }
  };

  <template>
    <EditPopover
      @title={{concat (t "common.revert") " " (formatCamelCase @fieldName)}}
      @icon="revert"
      @size="lg"
      @isRunning={{this.revertTask.isRunning}}
      @error={{this.error}}
      @submitLabel={{t "common.revert"}}
      @onSubmit={{this.handleSubmit}}
      data-test-update-override-revert
    >
      <:description>
        <span class="tio-fg-muted text-sm">
          {{t "common.employees.fields.revert_disclaimer"}}
        </span>
      </:description>
    </EditPopover>
  </template>
}

interface EditContributionAmountSignature {
  Args: {
    value: string;
    employee: EmployeeModel;
    isOverride: boolean;
  };
  Element: HTMLDivElement;
}

class EditContributionAmount extends Component<EditContributionAmountSignature> {
  @tracked isOpen = false;
  @tracked editableValue!: string;
  @tracked reasonForEdit = '';
  @tracked submitError?: string;
  @tracked didSubmit = false;

  @service declare store: Store;
  @service declare notifications: NotificationsService;
  @service declare intl: Intl;

  constructor(owner: Owner, args: EditContributionAmountSignature['Args']) {
    super(owner, args);
    this.syncEditableValue();
  }

  syncEditableValue() {
    this.editableValue = this.args.value;
    this.reasonForEdit = '';
  }

  get isValid() {
    return !!this.reasonForEdit && !!this.editableValue && this.editableValue !== this.args.value;
  }

  handleOpen = () => {
    this.isOpen = true;
  };

  updateReasonForEdit = (reason: string) => {
    this.reasonForEdit = reason;
  };

  setEditableValue = (value: string) => {
    this.editableValue = value;
  };

  cancel = () => {
    this.submitError = '';
    this.didSubmit = false;
    this.isOpen = false;
    this.syncEditableValue();
  };

  async updateContributionAmount() {
    await this.store.request(
      updateOverridesRequest(
        this.args.employee.id!,
        { field: 'contributionAmount', value: this.editableValue },
        this.reasonForEdit
      )
    );
    await reloadRecord(this.store, this.args.employee, { include: 'employee-addition' });
    const successMessage = this.intl.t('common.employees.fields.update_success', {
      field: this.intl.t('common.employees.fields.contribution_amount'),
    });
    this.notifications.add(successMessage, {
      appearance: 'success',
    });
  }

  updateTask = dropTask(async () => {
    this.submitError = '';
    this.didSubmit = false;
    try {
      await this.updateContributionAmount();
      this.isOpen = false;
      this.syncEditableValue();
    } catch (e) {
      console.error(e);
      this.didSubmit = true;
      this.submitError = this.intl.t('common.employees.fields.update_error', {
        field: this.intl.t('common.employees.fields.contribution_amount'),
      });
    }
  });

  <template>
    <Popover @isOpen={{this.isOpen}} @onOpen={{this.handleOpen}} @onClose={{this.cancel}}>
      <:trigger as |pop|>
        <Button
          @appearance="minimal"
          @intent="primary"
          title={{t "common.edit"}}
          {{pop.register}}
          {{on "click" pop.toggle}}
          data-test-update-override
        >
          <PhPencil @size="16" />
        </Button>
      </:trigger>
      <:body>
        <form class="tio-col-loose p-4">
          <h2 class="tio-text-xl-semibold">
            {{#if @isOverride}}
              {{t "common.edit"}}
            {{else}}
              {{t "common.add"}}
            {{/if}}
            {{t "common.employees.fields.custom_contribution_amount"}}</h2>
          <span class="tio-fg-muted text-sm">
            {{t "common.employees.fields.contribution_amount_disclaimer"}}
          </span>
          <Input
            data-test-update-override-input
            @isDisabled={{this.updateTask.isRunning}}
            @value={{this.editableValue}}
            @label={{t "common.employees.fields.contribution_amount"}}
            @description={{t "common.employees.fields.contribution_amount_input_description"}}
            @onChange={{this.setEditableValue}}
          />
          <Textarea
            data-test-reason-for-edit
            disabled={{this.updateTask.isRunning}}
            @value={{this.reasonForEdit}}
            @onChange={{this.updateReasonForEdit}}
            @label={{concat (t "common.comment") "*"}}
            @classes={{hash base="text-xs"}}
          />
          <div class="tio-flex-tight justify-end">
            <Button
              data-test-update-override-cancel
              class="w-auto"
              @intent="danger"
              {{on "click" this.cancel}}
              disabled={{this.updateTask.isRunning}}
              title={{t "common.cancel"}}
            >
              <PhX @size="16" />
            </Button>
            <Button
              data-test-update-override-save
              class="w-auto"
              @intent="primary"
              {{on "click" this.updateTask.perform}}
              @isRunning={{this.updateTask.isRunning}}
              disabled={{not this.isValid}}
              title={{t "common.save"}}
            >
              <PhCheck @size="16" />
            </Button>
          </div>
          <ErrorMessages
            @showErrors={{this.didSubmit}}
            @error={{this.submitError}}
            class="text-xs"
          />
        </form>
      </:body>
    </Popover>
  </template>
}

interface EditContributionPlanKeySignature {
  Args: {
    employee: EmployeeModel;
    value: string;
  };
  Element: HTMLDivElement;
}

class EditContributionPlanKey extends Component<EditContributionPlanKeySignature> {
  @tracked isOpen = false;
  @tracked selectedKey!: string;
  @tracked reasonForEdit = '';
  @tracked submitError?: string;
  @tracked didSubmit = false;

  @service declare store: Store;
  @service declare notifications: NotificationsService;
  @service declare intl: Intl;

  constructor(owner: Owner, args: EditContributionPlanKeySignature['Args']) {
    super(owner, args);
    this.syncSelectedKey();
  }

  syncSelectedKey() {
    this.selectedKey = this.args.value;
    this.reasonForEdit = '';
  }

  get isValid() {
    return !!this.reasonForEdit && !!this.selectedKey && this.selectedKey !== this.args.value;
  }

  handleOpen = () => {
    this.isOpen = true;
  };

  updateReasonForEdit = (reason: string) => {
    this.reasonForEdit = reason;
  };

  setSelectedKey = (value: string) => {
    this.selectedKey = value;
  };

  cancel = () => {
    this.submitError = '';
    this.didSubmit = false;
    this.isOpen = false;
    this.syncSelectedKey();
  };

  async updateContributionPlanKey() {
    await this.store.request(
      updateOverridesRequest(
        this.args.employee.id!,
        { field: 'contributionPlanKey', value: this.selectedKey },
        this.reasonForEdit
      )
    );
    await reloadRecord(this.store, this.args.employee, { include: 'employee-addition,plan' });
    const successMessage = this.intl.t('common.employees.fields.update_success', {
      field: this.intl.t('common.employees.fields.contribution_plan_key'),
    });
    this.notifications.add(successMessage, {
      appearance: 'success',
    });
  }

  updateTask = dropTask(async () => {
    this.submitError = '';
    this.didSubmit = false;
    try {
      await this.updateContributionPlanKey();
      this.isOpen = false;
      this.syncSelectedKey();
    } catch (e) {
      console.error(e);
      this.didSubmit = true;
      this.submitError = this.intl.t('common.employees.fields.update_error', {
        field: this.intl.t('common.employees.fields.contribution_plan_key'),
      });
    }
  });

  get planOptions(): { key: string; label: string; description: string }[] {
    return (
      this.plansData.value?.map((plan: PlanModel) => ({
        key: plan.companyKey,
        label: plan.companyKey,
        description: plan.name,
      })) || []
    );
  }

  plansData = trackedFunction(this, async () => {
    const companyId = this.args.employee?.company?.id;
    if (!companyId) {
      return;
    }
    const { content } = (await this.store.request(
      findRecord<CompanyModel>('company', companyId, {
        include: 'plans',
        reload: true,
      })
    )) as { content: { data: CompanyModel } };
    return content.data.plans;
  });

  <template>
    <Popover @isOpen={{this.isOpen}} @onOpen={{this.handleOpen}} @onClose={{this.cancel}}>
      <:trigger as |pop|>
        <Button
          @appearance="minimal"
          @intent="primary"
          title={{t "common.edit"}}
          {{pop.register}}
          {{on "click" pop.toggle}}
          data-test-update-override
        >
          <PhPencil @size="16" />
        </Button>
      </:trigger>
      <:body>
        <form class="tio-col-loose p-4">
          <h2 class="tio-text-xl-semibold">{{t "common.edit"}}
            {{t "common.employees.fields.contribution_plan_key"}}
          </h2>
          <Select
            data-test-update-contribution-key-select
            @disabled={{or this.plansData.isLoading this.updateTask.isRunning}}
            @selected={{this.selectedKey}}
            @label={{t "common.employees.fields.contribution_plan_key"}}
            @onChange={{fn (mut this.selectedKey)}}
            @items={{this.planOptions}}
          />
          <Textarea
            data-test-reason-for-edit
            disabled={{this.updateTask.isRunning}}
            @value={{this.reasonForEdit}}
            @onChange={{this.updateReasonForEdit}}
            @label={{concat (t "common.comment") "*"}}
            @classes={{hash base="text-xs"}}
          />
          <div class="tio-flex-tight justify-end">
            <Button
              data-test-update-override-cancel
              class="w-auto"
              @intent="danger"
              {{on "click" this.cancel}}
              disabled={{this.updateTask.isRunning}}
              title={{t "common.cancel"}}
            >
              <PhX @size="16" />
            </Button>
            <Button
              data-test-update-override-save
              class="w-auto"
              @intent="primary"
              {{on "click" this.updateTask.perform}}
              @isRunning={{this.updateTask.isRunning}}
              disabled={{not this.isValid}}
              title={{t "common.save"}}
            >
              <PhCheck @size="16" />
            </Button>
          </div>
          <ErrorMessages
            @showErrors={{this.didSubmit}}
            @error={{this.submitError}}
            class="text-xs"
          />
        </form>
      </:body>
    </Popover>
  </template>
}
